import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Card, Row, Col, Space, Spin, Button, Empty, Tag, Table, Badge, message } from 'antd';
import {
  CheckCircleOutlined, ClockCircleOutlined,
  ThunderboltOutlined, RiseOutlined, DashboardOutlined, ReloadOutlined,
  UserOutlined, GlobalOutlined, FireOutlined, FundOutlined, ApiOutlined,
  FieldTimeOutlined, TeamOutlined, EyeOutlined, BarChartOutlined,
  CameraOutlined, DragOutlined
} from '@ant-design/icons';
import {
  PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, LineChart, Line,
  AreaChart, Area, RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
} from 'recharts';
import { ComposableMap, Geographies, Geography, Marker, ZoomableGroup } from 'react-simple-maps';
import html2canvas from 'html2canvas';
import { testApi } from '../services/api';
import { getChannelLogo } from '../utils/channelLogos';
import { useLanguage } from '../contexts/LanguageContext';
import dayjs from 'dayjs';

var GEO_URL = 'https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json';

var CHANNEL_GEO = {
  EQUALS_MONEY: { lat: 51.5, lng: -0.12, country: 'UK' },
  PAGSMILE: { lat: -15.8, lng: -47.9, country: 'Brazil' },
  SGB: { lat: 1.35, lng: 103.82, country: 'Singapore' },
  LEAN_TECH: { lat: 24.45, lng: 54.65, country: 'UAE' },
  PYVIO: { lat: 39.9, lng: 116.4, country: 'China' },
  PALMPAY: { lat: 6.52, lng: 3.38, country: 'Nigeria' },
  KORAPAY: { lat: 6.52, lng: 3.38, country: 'Nigeria' },
  DURIAN_PAY: { lat: -6.2, lng: 106.85, country: 'Indonesia' },
  BANK_FRICK: { lat: 47.17, lng: 9.51, country: 'Liechtenstein' },
  BANKING_CIRCLE: { lat: 49.61, lng: 6.13, country: 'Luxembourg' },
  BITSO: { lat: 19.43, lng: -99.13, country: 'Mexico' },
  KKI: { lat: -6.2, lng: 106.85, country: 'Indonesia' },
  XENDIT: { lat: -6.2, lng: 106.85, country: 'Indonesia' },
};

var SC = { SUCCESS: '#52c41a', FAILED: '#ff4d4f', TIMEOUT: '#faad14' };
var CL = ['#667eea','#764ba2','#f093fb','#4facfe','#43e97b','#fa709a','#fee140','#30cfd0','#a18cd1','#fbc2eb','#ff9a9e','#fad0c4'];
var RAD = Math.PI / 180;
var GC = [
  'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
  'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
  'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
  'linear-gradient(135deg, #a18cd1 0%, #fbc2eb 100%)',
  'linear-gradient(135deg, #ff9a9e 0%, #fad0c4 100%)',
];
var TT = { borderRadius: 8, border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' };

function PieLabel(p) {
  if (p.percent < 0.04) return null;
  var r = p.outerRadius + 20;
  var x = p.cx + r * Math.cos(-p.midAngle * RAD);
  var y = p.cy + r * Math.sin(-p.midAngle * RAD);
  return React.createElement('text', { x: x, y: y, fill: '#555', textAnchor: x > p.cx ? 'start' : 'end', dominantBaseline: 'central', fontSize: 11, fontWeight: 500 }, p.name + ' ' + (p.percent * 100).toFixed(0) + '%');
}

function RPie(props) {
  return (
    <ResponsiveContainer width="100%" height={250}>
      <PieChart>
        <Pie data={props.items} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={50} outerRadius={85} label={PieLabel} labelLine={false} stroke="none" animationDuration={800}>
          {props.items.map(function(e, i) { return <Cell key={i} fill={props.colorMap ? (props.colorMap[e.name] || CL[i % CL.length]) : CL[i % CL.length]} />; })}
        </Pie>
        <Tooltip contentStyle={TT} />
        <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8 }} />
      </PieChart>
    </ResponsiveContainer>
  );
}

function CC(props) {
  return (
    <Card className="modern-card" bodyStyle={{ padding: '16px' }} style={{ borderRadius: 12, boxShadow: '0 2px 12px rgba(0,0,0,0.06)', height: props.h || 'auto' }} title={<Space size={8}>{props.icon}<span style={{ fontSize: 14, fontWeight: 600, color: '#374151' }}>{props.title}</span></Space>}>
      {props.children}
    </Card>
  );
}

/* ---- channel color palette for map markers ---- */
var MAP_COLORS = {
  'Equals Money': '#00e5a0', 'PagSmile': '#ff9f43', 'SGB': '#54a0ff',
  'Lean Tech': '#c56cf0', 'Pyvio': '#ff6b81', 'PalmPay': '#00d2d3',
  'KoraPay': '#ff6348', 'Durian Pay': '#7bed9f', 'Bank Frick': '#a4b0be',
  'Banking Circle': '#70a1ff', 'Bitso': '#ffa502', 'KKI': '#2ed573', 'Xendit': '#1e90ff',
};

/* label offset presets — every channel gets an explicit offset to guarantee no overlaps */
var LABEL_OFFSETS = {
  /* Nigeria cluster (lat 6.52, lng 3.38) — fan out to the left */
  'PalmPay':        { dx: -75, dy: -14 },
  'KoraPay':        { dx: -75, dy: 10 },
  /* Indonesia cluster (lat -6.2, lng 106.85) — fan out to the right */
  'Durian Pay':     { dx: 30, dy: -28 },
  'KKI':            { dx: 30, dy: -4 },
  'Xendit':         { dx: 30, dy: 20 },
  /* Europe cluster — Bank Frick (Liechtenstein) & Banking Circle (Luxembourg) are close */
  'Bank Frick':     { dx: 30, dy: -20 },
  'Banking Circle': { dx: 30, dy: 6 },
  /* standalone channels — offset above by default */
  'Equals Money':   { dx: 0, dy: -24 },
  'PagSmile':       { dx: 0, dy: -24 },
  'SGB':            { dx: 0, dy: -24 },
  'Lean Tech':      { dx: 0, dy: -24 },
  'Pyvio':          { dx: 0, dy: -24 },
  'Bitso':          { dx: 0, dy: -24 },
};

function WMap(props) {
  /* build a lookup of channels that have execution data */
  var dataMap = {};
  (props.channels || []).forEach(function(ch) {
    dataMap[ch.code] = ch;
  });

  /* always show ALL channels from CHANNEL_GEO, fill count=0 for missing ones */
  var allMarkers = [];
  var groupCount = {};
  Object.keys(CHANNEL_GEO).forEach(function(code) {
    var geo = CHANNEL_GEO[code];
    var ch = dataMap[code];
    var name = ch ? ch.name : (getChannelLogo(code).name || code);
    var count = ch ? ch.count : 0;
    var gk = geo.lat + ',' + geo.lng;
    groupCount[gk] = (groupCount[gk] || 0) + 1;
    allMarkers.push({ lat: geo.lat, lng: geo.lng, name: name, code: code, count: count, country: geo.country });
  });
  var totalCount = allMarkers.reduce(function(s, m) { return s + m.count; }, 0);

  /* deduplicate for dot rendering (one dot per location) */
  var dotMap = {};
  allMarkers.forEach(function(m) {
    var gk = m.lat + ',' + m.lng;
    if (!dotMap[gk]) dotMap[gk] = { lat: m.lat, lng: m.lng, count: 0, country: m.country };
    dotMap[gk].count += m.count;
  });
  var dots = Object.values(dotMap);

  return (
    <div style={{ background: 'linear-gradient(135deg, #020617 0%, #0f172a 40%, #1a1a3e 100%)', borderRadius: 16, overflow: 'hidden', position: 'relative' }}>
      {/* subtle grid overlay */}
      <div style={{ position: 'absolute', inset: 0, opacity: 0.04, backgroundImage: 'linear-gradient(rgba(255,255,255,.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.5) 1px, transparent 1px)', backgroundSize: '40px 40px', pointerEvents: 'none', zIndex: 0 }} />
      {/* top glow */}
      <div style={{ position: 'absolute', top: -80, left: '50%', transform: 'translateX(-50%)', width: 500, height: 160, background: 'radial-gradient(ellipse, rgba(102,126,234,0.15) 0%, transparent 70%)', pointerEvents: 'none', zIndex: 0 }} />

      <div style={{ display: 'flex', position: 'relative', zIndex: 1 }}>
        {/* map area */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <ComposableMap projectionConfig={{ scale: 155, center: [15, 5] }} style={{ width: '100%', height: 420 }}>
            <defs>
              <radialGradient id="mapGlow">
                <stop offset="0%" stopColor="#667eea" stopOpacity="0.6" />
                <stop offset="100%" stopColor="#667eea" stopOpacity="0" />
              </radialGradient>
              <filter id="glow">
                <feGaussianBlur stdDeviation="3" result="blur" />
                <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
              </filter>
              <filter id="softGlow">
                <feGaussianBlur stdDeviation="2" result="blur" />
                <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
              </filter>
            </defs>
            <ZoomableGroup>
              <Geographies geography={GEO_URL}>
                {function(ref) {
                  return ref.geographies.map(function(geo) {
                    return <Geography key={geo.rsmKey} geography={geo} fill="#1e293b" stroke="#334155" strokeWidth={0.4} style={{ default: { outline: 'none' }, hover: { fill: '#263548', outline: 'none', transition: 'fill 0.3s' }, pressed: { outline: 'none' } }} />;
                  });
                }}
              </Geographies>

              {/* connection arcs from center to each dot */}
              {dots.map(function(d, i) {
                var cx = 15, cy = 5;
                var mx = (cx + d.lng) / 2, my = (cy + d.lat) / 2 - 15;
                return (
                  <Marker key={'arc' + i} coordinates={[0, 0]}>
                    <path
                      d={'M ' + (cx * 1.7) + ' ' + (cy * -1.7) + ' Q ' + (mx * 1.7) + ' ' + (my * -1.7) + ' ' + (d.lng * 1.7) + ' ' + (d.lat * -1.7)}
                      fill="none" stroke="#667eea" strokeWidth={0.5} strokeOpacity={0.15} strokeDasharray="4 4"
                    />
                  </Marker>
                );
              })}

              {/* location dots with glow */}
              {dots.map(function(d, i) {
                var hasData = d.count > 0;
                var r = hasData ? Math.max(5, Math.min(14, Math.sqrt(d.count) * 3)) : 4;
                return (
                  <Marker key={'dot' + i} coordinates={[d.lng, d.lat]}>
                    {/* outer glow */}
                    <circle r={r + 12} fill="url(#mapGlow)" opacity={hasData ? 0.5 : 0.2} />
                    {/* pulse ring 1 */}
                    {hasData && <circle r={r} fill="none" stroke="#667eea" strokeWidth={1.5} opacity={0.6}>
                      <animate attributeName="r" from={r} to={r + 18} dur="2.5s" repeatCount="indefinite" />
                      <animate attributeName="opacity" from="0.6" to="0" dur="2.5s" repeatCount="indefinite" />
                    </circle>}
                    {/* pulse ring 2 (offset) */}
                    {hasData && <circle r={r} fill="none" stroke="#a78bfa" strokeWidth={1} opacity={0.4}>
                      <animate attributeName="r" from={r} to={r + 24} dur="3s" begin="0.8s" repeatCount="indefinite" />
                      <animate attributeName="opacity" from="0.4" to="0" dur="3s" begin="0.8s" repeatCount="indefinite" />
                    </circle>}
                    {/* core dot */}
                    <circle r={r} fill={hasData ? '#667eea' : '#475569'} fillOpacity={hasData ? 0.85 : 0.6} stroke={hasData ? '#a5b4fc' : '#64748b'} strokeWidth={1.5} filter={hasData ? 'url(#glow)' : undefined} />
                    {/* bright center */}
                    {hasData && <circle r={r * 0.35} fill="#e0e7ff" fillOpacity={0.9} />}
                  </Marker>
                );
              })}

              {/* individual channel labels with smart offsets */}
              {allMarkers.map(function(m, i) {
                var off = LABEL_OFFSETS[m.name] || { dx: 0, dy: -24 };
                var color = MAP_COLORS[m.name] || '#a5b4fc';
                var gk = m.lat + ',' + m.lng;
                var isCluster = groupCount[gk] > 1;
                var anchor = off.dx > 0 ? 'start' : off.dx < 0 ? 'end' : 'middle';
                /* compute pill x position based on anchor */
                var pillW = 80;
                var pillX = anchor === 'end' ? off.dx - pillW + 2 : anchor === 'start' ? off.dx - 2 : off.dx - pillW / 2;
                var textX = anchor === 'end' ? off.dx - pillW / 2 + 2 : anchor === 'start' ? off.dx + pillW / 2 - 2 : off.dx;
                return (
                  <Marker key={'lbl' + i} coordinates={[m.lng, m.lat]}>
                    {/* connector line */}
                    {isCluster && <line x1={0} y1={0} x2={off.dx} y2={off.dy} stroke={color} strokeWidth={0.7} strokeOpacity={0.35} strokeDasharray="2 2" />}
                    {/* label bg pill */}
                    <rect x={pillX} y={off.dy - 9} width={pillW} height={18} rx={9} fill="rgba(0,0,0,0.6)" stroke={color} strokeWidth={0.6} strokeOpacity={0.4} />
                    {/* channel name */}
                    <text x={textX} y={off.dy + 4} textAnchor="middle" style={{ fontSize: 9, fill: color, fontWeight: 700, letterSpacing: 0.3, textShadow: '0 0 6px ' + color }} filter="url(#softGlow)">{m.name}</text>
                    {/* count badge for channels with data */}
                    {m.count > 0 && <circle cx={pillX + pillW - 2} cy={off.dy - 6} r={7} fill={color} fillOpacity={0.9} />}
                    {m.count > 0 && <text x={pillX + pillW - 2} y={off.dy - 3} textAnchor="middle" style={{ fontSize: 7, fill: '#fff', fontWeight: 800 }}>{m.count}</text>}
                  </Marker>
                );
              })}
            </ZoomableGroup>
          </ComposableMap>
        </div>

        {/* right side legend panel */}
        <div style={{ width: 200, padding: '20px 16px 20px 0', display: 'flex', flexDirection: 'column', justifyContent: 'center', flexShrink: 0 }}>
          <div style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: 2, marginBottom: 6, fontWeight: 700 }}>Global Channels</div>
          <div style={{ fontSize: 28, color: '#e2e8f0', fontWeight: 800, marginBottom: 2 }}>{allMarkers.length}</div>
          <div style={{ fontSize: 10, color: '#475569', marginBottom: 16 }}>{allMarkers.filter(function(m) { return m.count > 0; }).length} active · {totalCount} executions</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 300, overflowY: 'auto' }}>
            {allMarkers.slice().sort(function(a, b) { return b.count - a.count; }).map(function(m, i) {
              var color = MAP_COLORS[m.name] || '#a5b4fc';
              var pct = totalCount > 0 ? Math.round((m.count / totalCount) * 100) : 0;
              var active = m.count > 0;
              return (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', borderRadius: 8, background: active ? 'rgba(255,255,255,0.03)' : 'transparent', border: '1px solid ' + (active ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.02)'), opacity: active ? 1 : 0.5, transition: 'all 0.2s' }}>
                  <div style={{ width: 8, height: 8, borderRadius: '50%', background: color, boxShadow: active ? '0 0 6px ' + color : 'none', flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 10, color: active ? '#e2e8f0' : '#64748b', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{m.name}</div>
                    <div style={{ fontSize: 9, color: '#64748b' }}>{m.country}</div>
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <div style={{ fontSize: 12, color: active ? color : '#475569', fontWeight: 800 }}>{m.count}</div>
                    {active && <div style={{ fontSize: 8, color: '#475569' }}>{pct}%</div>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* bottom stats bar */}
      <div style={{ display: 'flex', justifyContent: 'center', gap: 32, padding: '10px 0 14px', borderTop: '1px solid rgba(255,255,255,0.05)' }}>
        {[
          { label: 'Regions', value: dots.length, color: '#667eea' },
          { label: 'Channels', value: allMarkers.length, color: '#a78bfa' },
          { label: 'Executions', value: totalCount, color: '#34d399' },
        ].map(function(s, i) {
          return (
            <div key={i} style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 18, fontWeight: 800, color: s.color, textShadow: '0 0 12px ' + s.color + '66' }}>{s.value}</div>
              <div style={{ fontSize: 9, color: '#64748b', textTransform: 'uppercase', letterSpacing: 1.5 }}>{s.label}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function Dashboard() {
  var lang = useLanguage(); var t = lang.t;
  var ref0 = useState(false); var loading = ref0[0], setLoading = ref0[1];
  var ref1 = useState([]); var data = ref1[0], setData = ref1[1];
  var dashRef = useRef(null);

  var defaultOrder = ['map','pie1','pie2','pie3','pie4','pie5','pvuv','daily','cost','chRate','hourly','chStatus','chType','executor','radar','recent'];
  var ref2 = useState(defaultOrder); var chartOrder = ref2[0], setChartOrder = ref2[1];
  var ref3 = useState(null); var dragId = ref3[0], setDragId = ref3[1];

  useEffect(function() { loadData(); }, []);
  function loadData() {
    setLoading(true);
    testApi.getExecutions().then(function(r) { setData(r.data.data || []); }).catch(function() {}).finally(function() { setLoading(false); });
  }

  var exportPNG = useCallback(function() {
    if (!dashRef.current) return;
    message.loading({ content: 'Exporting...', key: 'exp' });
    html2canvas(dashRef.current, { scale: 2, useCORS: true, backgroundColor: '#f0f2f5', logging: false }).then(function(canvas) {
      var link = document.createElement('a');
      link.download = 'Dashboard_Report_' + dayjs().format('YYYYMMDD_HHmmss') + '.png';
      link.href = canvas.toDataURL('image/png');
      link.click();
      message.success({ content: 'Exported!', key: 'exp' });
    }).catch(function() { message.error({ content: 'Export failed', key: 'exp' }); });
  }, []);

  function onDragStart(e, id) { setDragId(id); e.dataTransfer.effectAllowed = 'move'; }
  function onDragOver(e) { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; }
  function onDrop(e, targetId) {
    e.preventDefault();
    if (!dragId || dragId === targetId) return;
    setChartOrder(function(prev) {
      var arr = prev.slice();
      var fi = arr.indexOf(dragId), ti = arr.indexOf(targetId);
      if (fi < 0 || ti < 0) return prev;
      arr.splice(fi, 1);
      arr.splice(ti, 0, dragId);
      return arr;
    });
    setDragId(null);
  }

  /* ---- computed data ---- */
  var stats = useMemo(function() {
    if (!data.length) return null;
    var n = data.length, s = 0, f = 0, to = 0, cost = 0;
    var today = dayjs().format('YYYY-MM-DD'), tc = 0;
    var exSet = new Set(), chSet = new Set();
    data.forEach(function(d) { if (d.status === 'SUCCESS') s++; if (d.status === 'FAILED') f++; if (d.status === 'TIMEOUT') to++; cost += (d.costMs || 0); if (dayjs(d.createTime).format('YYYY-MM-DD') === today) tc++; exSet.add(d.executor); chSet.add(d.channelCode); });
    return { total: n, success: s, failed: f, timeout: to, avgCost: Math.round(cost / n), todayCount: tc, successRate: ((s / n) * 100).toFixed(1), uv: exSet.size, channels: chSet.size };
  }, [data]);

  var testTypeData = useMemo(function() { var m = {}; data.forEach(function(d) { m[d.testType] = (m[d.testType] || 0) + 1; }); return Object.entries(m).map(function(e) { return { name: e[0], value: e[1] }; }); }, [data]);
  var channelData = useMemo(function() { var m = {}; data.forEach(function(d) { m[d.channelCode] = (m[d.channelCode] || 0) + 1; }); return Object.entries(m).map(function(e) { return { name: getChannelLogo(e[0]).name, value: e[1] }; }).sort(function(a, b) { return b.value - a.value; }); }, [data]);
  var statusData = useMemo(function() { var m = {}; data.forEach(function(d) { m[d.status] = (m[d.status] || 0) + 1; }); return Object.entries(m).map(function(e) { return { name: e[0], value: e[1] }; }); }, [data]);
  var currencyData = useMemo(function() { var m = {}; data.forEach(function(d) { if (d.currency) m[d.currency] = (m[d.currency] || 0) + 1; }); return Object.entries(m).map(function(e) { return { name: e[0], value: e[1] }; }); }, [data]);
  var envData = useMemo(function() { var m = {}; data.forEach(function(d) { m[d.env || 'unknown'] = (m[d.env || 'unknown'] || 0) + 1; }); return Object.entries(m).map(function(e) { return { name: e[0], value: e[1] }; }); }, [data]);

  var mapChannels = useMemo(function() {
    var m = {}; data.forEach(function(d) { m[d.channelCode] = (m[d.channelCode] || 0) + 1; });
    return Object.entries(m).map(function(e) { return { code: e[0], name: getChannelLogo(e[0]).name, count: e[1] }; });
  }, [data]);

  var pvUvTrend = useMemo(function() {
    var m = {}; for (var i = 29; i >= 0; i--) { var d = dayjs().subtract(i, 'day').format('MM-DD'); m[d] = { date: d, pv: 0, ex: new Set() }; }
    data.forEach(function(d) { var day = dayjs(d.createTime).format('MM-DD'); if (m[day]) { m[day].pv++; m[day].ex.add(d.executor); } });
    return Object.values(m).map(function(v) { return { date: v.date, PV: v.pv, UV: v.ex.size }; });
  }, [data]);

  var dailyTrend = useMemo(function() {
    var m = {}; for (var i = 29; i >= 0; i--) { var d = dayjs().subtract(i, 'day').format('MM-DD'); m[d] = { date: d, SUCCESS: 0, FAILED: 0 }; }
    data.forEach(function(d) { var day = dayjs(d.createTime).format('MM-DD'); if (m[day] && m[day][d.status] !== undefined) m[day][d.status]++; });
    return Object.values(m);
  }, [data]);

  var costDist = useMemo(function() {
    var b = [{ range: '<1s', count: 0, fill: '#52c41a' }, { range: '1-3s', count: 0, fill: '#faad14' }, { range: '3-5s', count: 0, fill: '#ff7a45' }, { range: '>5s', count: 0, fill: '#ff4d4f' }];
    data.forEach(function(d) { var ms = d.costMs || 0; if (ms < 1000) b[0].count++; else if (ms < 3000) b[1].count++; else if (ms < 5000) b[2].count++; else b[3].count++; });
    return b;
  }, [data]);

  var chTestType = useMemo(function() {
    var cm = {}, ts = new Set();
    data.forEach(function(d) { var c = getChannelLogo(d.channelCode).name; if (!cm[c]) cm[c] = {}; cm[c][d.testType] = (cm[c][d.testType] || 0) + 1; ts.add(d.testType); });
    return { data: Object.entries(cm).map(function(e) { var o = { channel: e[0] }; Object.keys(e[1]).forEach(function(k) { o[k] = e[1][k]; }); return o; }), types: Array.from(ts) };
  }, [data]);

  var executorData = useMemo(function() { var m = {}; data.forEach(function(d) { m[d.executor] = (m[d.executor] || 0) + 1; }); return Object.entries(m).map(function(e) { return { name: e[0], count: e[1] }; }).sort(function(a, b) { return b.count - a.count; }).slice(0, 10).reverse(); }, [data]);

  var chSuccessRate = useMemo(function() {
    var m = {}; data.forEach(function(d) { var c = getChannelLogo(d.channelCode).name; if (!m[c]) m[c] = { t: 0, s: 0 }; m[c].t++; if (d.status === 'SUCCESS') m[c].s++; });
    return Object.entries(m).map(function(e) { return { name: e[0], rate: Number(((e[1].s / e[1].t) * 100).toFixed(1)), total: e[1].t }; }).sort(function(a, b) { return a.rate - b.rate; });
  }, [data]);

  var hourlyData = useMemo(function() { var h = []; for (var i = 0; i < 24; i++) { h.push({ hour: (i < 10 ? '0' : '') + i + ':00', count: 0 }); } data.forEach(function(d) { h[dayjs(d.createTime).hour()].count++; }); return h; }, [data]);

  var chStatusData = useMemo(function() { var cm = {}; data.forEach(function(d) { var c = getChannelLogo(d.channelCode).name; if (!cm[c]) cm[c] = { channel: c, SUCCESS: 0, FAILED: 0, TIMEOUT: 0 }; if (cm[c][d.status] !== undefined) cm[c][d.status]++; }); return Object.values(cm); }, [data]);

  var radarData = useMemo(function() {
    var m = {}; data.forEach(function(d) { var c = getChannelLogo(d.channelCode).name; if (!m[c]) m[c] = { channel: c, executions: 0, successRate: 0, avgSpeed: 0, _s: 0, _cost: 0 }; m[c].executions++; m[c]._cost += (d.costMs || 0); if (d.status === 'SUCCESS') m[c]._s++; });
    return Object.values(m).map(function(v) { v.successRate = Math.round((v._s / v.executions) * 100); v.avgSpeed = Math.max(0, 100 - Math.round(v._cost / v.executions / 50)); return v; });
  }, [data]);

  var recentData = useMemo(function() { return data.slice().sort(function(a, b) { return new Date(b.createTime || 0) - new Date(a.createTime || 0); }).slice(0, 8); }, [data]);

  /* ---- loading / empty ---- */
  if (loading) return <div style={{display:'flex',justifyContent:'center',alignItems:'center',height:'calc(100vh - 64px)'}}><Spin size="large" /></div>;
  if (!data.length) return <div style={{padding:'24px 32px'}}><Card><Empty description={t('noData')} /></Card></div>;

  var kpi = [
    { title: t('totalExecutions'), value: stats.total, icon: <ThunderboltOutlined style={{fontSize:24,color:'#fff'}} />, bg: GC[0] },
    { title: t('successRate'), value: stats.successRate + '%', icon: <RiseOutlined style={{fontSize:24,color:'#fff'}} />, bg: GC[1] },
    { title: t('avgResponseTime'), value: stats.avgCost + 'ms', icon: <ClockCircleOutlined style={{fontSize:24,color:'#fff'}} />, bg: GC[2] },
    { title: t('todayExecutions'), value: stats.todayCount, icon: <FireOutlined style={{fontSize:24,color:'#fff'}} />, bg: GC[3] },
    { title: 'UV', value: stats.uv, icon: <UserOutlined style={{fontSize:24,color:'#fff'}} />, bg: GC[4] },
    { title: t('channelDistribution'), value: stats.channels, icon: <ApiOutlined style={{fontSize:24,color:'#fff'}} />, bg: GC[5] },
  ];

  var rcols = [
    { title: t('channel'), dataIndex: 'channelCode', width: 100, render: function(v) { var l = getChannelLogo(v); return <Tag color={l.color}>{l.emoji} {l.name}</Tag>; }},
    { title: t('testType'), dataIndex: 'testType', width: 120, render: function(v) { return <Tag color="blue">{v}</Tag>; }},
    { title: t('status'), dataIndex: 'status', width: 90, render: function(v) { return <Badge status={v === 'SUCCESS' ? 'success' : v === 'FAILED' ? 'error' : 'warning'} text={v} />; }},
    { title: t('costTime'), dataIndex: 'costMs', width: 80, render: function(v) { return <span style={{color: v > 5000 ? '#ff4d4f' : v > 2000 ? '#faad14' : '#52c41a', fontWeight: 600}}>{v}ms</span>; }},
    { title: t('executor'), dataIndex: 'executor', width: 80 },
    { title: t('executionTime'), dataIndex: 'createTime', width: 140, render: function(v) { return dayjs(v).format('MM-DD HH:mm:ss'); }},
  ];

  /* ---- chart definitions: id -> { span, render } ---- */
  var chartDefs = {
    map: { span: 24, render: function() {
      return <CC icon={<GlobalOutlined style={{ color: '#667eea' }} />} title={t('channelGlobalMap') || 'Channel Global Map'}><WMap channels={mapChannels} /></CC>;
    }},
    pie1: { span: 8, render: function() {
      return <CC icon={<ThunderboltOutlined style={{ color: '#667eea' }} />} title={t('testTypeDistribution')} h={340}><RPie items={testTypeData} /></CC>;
    }},
    pie2: { span: 8, render: function() {
      return <CC icon={<GlobalOutlined style={{ color: '#764ba2' }} />} title={t('channelDistribution')} h={340}><RPie items={channelData} /></CC>;
    }},
    pie3: { span: 8, render: function() {
      return <CC icon={<CheckCircleOutlined style={{ color: '#52c41a' }} />} title={t('statusDistribution')} h={340}><RPie items={statusData} colorMap={SC} /></CC>;
    }},
    pie4: { span: 12, render: function() {
      return <CC icon={<FundOutlined style={{ color: '#4facfe' }} />} title={t('currencyDistribution') || 'Currency'} h={340}><RPie items={currencyData} /></CC>;
    }},
    pie5: { span: 12, render: function() {
      return <CC icon={<ApiOutlined style={{ color: '#fa709a' }} />} title={t('envDistribution') || 'Environment'} h={340}><RPie items={envData} /></CC>;
    }},
    pvuv: { span: 24, render: function() {
      return (
        <CC icon={<EyeOutlined style={{ color: '#667eea' }} />} title={t('pvUvTrend') || 'PV/UV Trend (30 Days)'}>
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={pvUvTrend}>
              <defs>
                <linearGradient id="gPV" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#667eea" stopOpacity={0.3} /><stop offset="95%" stopColor="#667eea" stopOpacity={0} /></linearGradient>
                <linearGradient id="gUV" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#fa709a" stopOpacity={0.3} /><stop offset="95%" stopColor="#fa709a" stopOpacity={0} /></linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip contentStyle={TT} />
              <Legend />
              <Area type="monotone" dataKey="PV" stroke="#667eea" fill="url(#gPV)" strokeWidth={2} />
              <Area type="monotone" dataKey="UV" stroke="#fa709a" fill="url(#gUV)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </CC>
      );
    }},
    daily: { span: 24, render: function() {
      return (
        <CC icon={<RiseOutlined style={{ color: '#43e97b' }} />} title={t('dailyTrend')}>
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={dailyTrend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip contentStyle={TT} />
              <Legend />
              <Line type="monotone" dataKey="SUCCESS" stroke="#52c41a" strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 6 }} />
              <Line type="monotone" dataKey="FAILED" stroke="#ff4d4f" strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 6 }} />
            </LineChart>
          </ResponsiveContainer>
        </CC>
      );
    }},
    cost: { span: 12, render: function() {
      return (
        <CC icon={<ClockCircleOutlined style={{ color: '#faad14' }} />} title={t('responseTimeDistribution')}>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={costDist}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="range" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip contentStyle={TT} />
              <Bar dataKey="count" radius={[8, 8, 0, 0]} animationDuration={800}>
                {costDist.map(function(e, i) { return <Cell key={i} fill={e.fill} />; })}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </CC>
      );
    }},
    chRate: { span: 12, render: function() {
      return (
        <CC icon={<CheckCircleOutlined style={{ color: '#667eea' }} />} title={t('channelSuccessRate')}>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={chSuccessRate} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 11 }} unit="%" />
              <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={90} />
              <Tooltip contentStyle={TT} formatter={function(v) { return v + '%'; }} />
              <Bar dataKey="rate" radius={[0, 8, 8, 0]} animationDuration={800}>
                {chSuccessRate.map(function(e, i) { return <Cell key={i} fill={e.rate >= 80 ? '#52c41a' : e.rate >= 50 ? '#faad14' : '#ff4d4f'} />; })}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </CC>
      );
    }},
    hourly: { span: 12, render: function() {
      return (
        <CC icon={<FieldTimeOutlined style={{ color: '#30cfd0' }} />} title={t('hourlyDistribution') || 'Hourly Distribution'}>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={hourlyData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="hour" tick={{ fontSize: 10 }} interval={1} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip contentStyle={TT} />
              <Bar dataKey="count" radius={[4, 4, 0, 0]} animationDuration={800}>
                {hourlyData.map(function(e, i) { return <Cell key={i} fill={CL[i % CL.length]} />; })}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </CC>
      );
    }},
    chStatus: { span: 12, render: function() {
      return (
        <CC icon={<BarChartOutlined style={{ color: '#764ba2' }} />} title={t('channelStatusDistribution') || 'Channel x Status'}>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={chStatusData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="channel" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip contentStyle={TT} />
              <Legend />
              <Bar dataKey="SUCCESS" stackId="a" fill="#52c41a" />
              <Bar dataKey="FAILED" stackId="a" fill="#ff4d4f" />
              <Bar dataKey="TIMEOUT" stackId="a" fill="#faad14" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </CC>
      );
    }},
    chType: { span: 12, render: function() {
      return (
        <CC icon={<GlobalOutlined style={{ color: '#f093fb' }} />} title={t('channelTestTypeHeatmap')}>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={chTestType.data}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="channel" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip contentStyle={TT} />
              <Legend />
              {chTestType.types.map(function(tp, i) { return <Bar key={tp} dataKey={tp} fill={CL[i % CL.length]} radius={[4, 4, 0, 0]} />; })}
            </BarChart>
          </ResponsiveContainer>
        </CC>
      );
    }},
    executor: { span: 12, render: function() {
      return (
        <CC icon={<TeamOutlined style={{ color: '#fa709a' }} />} title={t('executorLeaderboard')}>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={executorData} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis type="number" tick={{ fontSize: 11 }} />
              <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={80} />
              <Tooltip contentStyle={TT} />
              <Bar dataKey="count" radius={[0, 8, 8, 0]} animationDuration={800}>
                {executorData.map(function(e, i) { return <Cell key={i} fill={CL[i % CL.length]} />; })}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </CC>
      );
    }},
    radar: { span: 10, render: function() {
      return (
        <CC icon={<FundOutlined style={{ color: '#a18cd1' }} />} title={t('channelRadar') || 'Channel Radar'}>
          <ResponsiveContainer width="100%" height={320}>
            <RadarChart data={radarData} cx="50%" cy="50%" outerRadius="70%">
              <PolarGrid stroke="#e0e0e0" />
              <PolarAngleAxis dataKey="channel" tick={{ fontSize: 10 }} />
              <PolarRadiusAxis tick={{ fontSize: 9 }} />
              <Radar name="Executions" dataKey="executions" stroke="#667eea" fill="#667eea" fillOpacity={0.3} />
              <Radar name="Success%" dataKey="successRate" stroke="#52c41a" fill="#52c41a" fillOpacity={0.2} />
              <Radar name="Speed" dataKey="avgSpeed" stroke="#fa709a" fill="#fa709a" fillOpacity={0.2} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Tooltip contentStyle={TT} />
            </RadarChart>
          </ResponsiveContainer>
        </CC>
      );
    }},
    recent: { span: 14, render: function() {
      return (
        <CC icon={<FieldTimeOutlined style={{ color: '#4facfe' }} />} title={t('recentExecutions') || 'Recent Executions'}>
          <Table dataSource={recentData} columns={rcols} pagination={false} size="small" rowKey={function(r, i) { return i; }} scroll={{ x: 600 }} style={{ fontSize: 12 }} />
        </CC>
      );
    }},
  };

  /* ---- build rows from chartOrder ---- */
  var rows = [];
  var curRow = [];
  var curSpan = 0;
  chartOrder.forEach(function(id) {
    var def = chartDefs[id];
    if (!def) return;
    if (curSpan + def.span > 24 && curRow.length > 0) {
      rows.push(curRow);
      curRow = [];
      curSpan = 0;
    }
    curRow.push({ id: id, span: def.span, render: def.render });
    curSpan += def.span;
    if (curSpan >= 24) {
      rows.push(curRow);
      curRow = [];
      curSpan = 0;
    }
  });
  if (curRow.length > 0) rows.push(curRow);

  var dragStyle = { cursor: 'grab', transition: 'transform 0.2s, box-shadow 0.2s' };
  var dragOverStyle = function(id) { return dragId && dragId !== id ? { opacity: 0.7 } : {}; };

  return (
    <div style={{ padding: '16px 24px', height: 'calc(100vh - 64px)', overflow: 'auto', background: '#f0f2f5' }}>
      <div ref={dashRef} className="fade-in-up">
        <div style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', padding: '16px 28px', borderRadius: 12, marginBottom: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'center', boxShadow: '0 8px 24px rgba(102,126,234,0.35)' }}>
          <Space size={12}>
            <DashboardOutlined style={{ color: '#fff', fontSize: 24 }} />
            <span style={{ fontSize: 22, fontWeight: 700, color: '#fff', letterSpacing: 1 }}>{t('dashboard')}</span>
            <Tag color="rgba(255,255,255,0.25)" style={{ color: '#fff', border: 'none', borderRadius: 12, padding: '2px 12px' }}>{data.length} records</Tag>
          </Space>
          <Space size={8}>
            <Button icon={<CameraOutlined />} onClick={exportPNG} style={{ background: 'rgba(255,255,255,0.15)', color: '#fff', border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8 }}>{t('exportReport') || 'Export PNG'}</Button>
            <Button icon={<ReloadOutlined />} onClick={loadData} type="text" style={{ color: '#fff', fontSize: 15 }}>{t('refresh')}</Button>
          </Space>
        </div>

        <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
          {kpi.map(function(item, i) {
            return (
              <Col xs={12} sm={8} md={4} key={i}>
                <Card bodyStyle={{ padding: 0 }} style={{ borderRadius: 12, overflow: 'hidden', border: 'none', boxShadow: '0 4px 16px rgba(0,0,0,0.08)' }}>
                  <div style={{ background: item.bg, padding: '20px 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{ background: 'rgba(255,255,255,0.2)', borderRadius: 12, padding: 10, display: 'flex' }}>{item.icon}</div>
                    <div>
                      <div style={{ color: 'rgba(255,255,255,0.85)', fontSize: 11, marginBottom: 4 }}>{item.title}</div>
                      <div style={{ color: '#fff', fontSize: 24, fontWeight: 800, lineHeight: 1 }}>{item.value}</div>
                    </div>
                  </div>
                </Card>
              </Col>
            );
          })}
        </Row>

        {rows.map(function(row, ri) {
          return (
            <Row gutter={[16, 16]} style={{ marginBottom: 16 }} key={ri}>
              {row.map(function(item) {
                return (
                  <Col span={item.span} key={item.id}>
                    <div
                      draggable
                      onDragStart={function(e) { onDragStart(e, item.id); }}
                      onDragOver={onDragOver}
                      onDrop={function(e) { onDrop(e, item.id); }}
                      style={Object.assign({}, dragStyle, dragOverStyle(item.id))}
                      title="Drag to reorder"
                    >
                      {item.render()}
                    </div>
                  </Col>
                );
              })}
            </Row>
          );
        })}
      </div>
    </div>
  );
}
